<template>
  <div class="document-upload">
    <div class="document-upload__labels">
      <BasicText
        weight="bold"
        color="secondary-600"
      >
        {{ natureCourant.intitule }}
        <BasicText
          v-if="messageMin"
          size="body-m"
          color="red"
        >
          {{ messageMin }}
        </BasicText>
      </BasicText>
      <BasicText
        size="body-s"
        color="neutral-500"
      >
        {{ description }}
      </BasicText>
    </div>
    <slot
      name="header-nature"
      v-bind="natureCourant"
    ></slot>
    <div class="document-upload__file-upload">
      <BasicButton
        label="Choisir des fichiers"
        @click="selectFilesStart"
        variant="outlined"
        type="secondary"
        :disabled="readonly"
        class="upload-item"
      />
      <template
        v-for="contentItem in fichiersCourant"
        :key="`${contentItem.type}_${contentItem.ordre}`"
      >
        <div
          v-if="contentItem.type === 'succes'"
          class="file-item upload-item"
        >
          <BasicText
            size="body-m"
            color="neutral-500"
            class="download"
            @click="() => ouvreFichierAsync(contentItem.fichier)"
            :title="contentItem.fichier.intitule"
          >
            {{ contentItem.fichier.intitule }}
          </BasicText>
          <BasicIcon
            v-if="!readonly"
            name="close"
            class="icon"
            @click="async () => retireFichierAsync(contentItem)"
          />
        </div>
        <div
          v-else
          class="error-item upload-item"
        >
          <BasicIcon
            name="danger"
            active
            class="icon"
          />
          <BasicText
            size="body-m"
            color="secondary-600"
            :title="contentItem.erreur.intitule"
            @click="showAlert(contentItem.erreur.message)"
          >
            {{ contentItem.erreur.intitule }}
          </BasicText>
          <BasicIcon
            name="close"
            class="icon"
            @click="async () => retireFichierAsync(contentItem)"
          />
        </div>
      </template>

      <div
        v-if="fichiersCourant.length === 0"
        class="no-file"
      >
        <BasicText
          size="body-m"
          color="neutral-500"
        >
          Aucun fichier chargé
        </BasicText>
      </div>
      <template v-if="canDeleteList">
        <div class="trash-container">
          <BasicButton
            icon-name="trash"
            size="medium"
            label="Retirer de la liste"
            @click="() => retireNature(nature)"
          />
        </div>
      </template>
      <input
        ref="fileInput"
        type="file"
        :accept="acceptedFileTypesString"
        :multiple="true"
        :hidden="true"
        @change="selectFilesEnd"
      />
    </div>
    <slot
      name="footer-nature"
      v-bind="natureCourant"
    ></slot>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, ref } from 'vue'
  import { ACCEPTED_FILE_TYPES, MAX_FILE_LENGTH, UPLOAD_FICHIER_INJECT } from './constants'
  import type { UseUploadFichiersResult } from './UploadFichiers.types'
  import { useDialog } from '@/features/interface/dialog'

  const props = defineProps<{ nature: string }>()
  const uploadFichiers = inject<UseUploadFichiersResult>(UPLOAD_FICHIER_INJECT)!
  const { natureRecord, readonly, content, ajouteFichiersAsync, retireFichierAsync, ouvreFichierAsync, retireNature } =
    uploadFichiers
  const fileInput = ref<HTMLInputElement | null>(null)

  const natureCourant = computed(() => natureRecord.value[props.nature])!
  const fichiersCourant = computed(() => content.value.filter((item) => item.nature === natureCourant.value.code))

  const description = computed(() => {
    const types = ACCEPTED_FILE_TYPES.join(', ')
    const max = natureCourant.value.nombreMax

    return `Poids : ${MAX_FILE_LENGTH}Mo maximum. Formats acceptés : ${types}. ${max} fichiers maximum.`
  })

  const messageMin = computed(() => {
    const min = natureCourant.value.nombreMin

    return min === 0 ? undefined : min === 1 ? `(${min} fichier minimum)` : `(${min} fichiers minimum)`
  })

  const acceptedFileTypesString = computed(() => ACCEPTED_FILE_TYPES.join(','))
  const canDeleteList = computed(() => !natureCourant.value.obligatoire)
  const selectFilesStart = () => fileInput.value?.click()
  const selectFilesEnd = async (event: Event) => {
    const files = (event.target as HTMLInputElement).files
    if (!files?.length) return
    await ajouteFichiersAsync(props.nature, files)
    fileInput.value!.value = ''
  }

  const showAlert = (message: string) => {
    const dialog = useDialog()
    dialog.showDialog({
      message,
      type: 'Error',
      closable: true,
    })
  }
</script>

<style scoped lang="less">
  .document-upload {
    display: flex;
    flex-direction: column;
    gap: @spacing-10;
    .document-upload__labels {
      display: flex;
      flex-direction: column;
    }

    .document-upload__file-upload {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: @spacing-20;

      .upload-item {
        width: 200px;
      }

      .trash-container {
        flex: auto;
        display: flex;
        justify-content: flex-end;
      }

      .error-item,
      .file-item {
        display: inline-flex;
        padding: 8px;
        align-items: center;
        justify-content: space-between;
        align-self: stretch;
        gap: 4px;
        border-radius: 8px;
        background-color: #f7f7f7;
        > span {
          flex: 1;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          &:hover {
            text-decoration: underline;
            cursor: pointer;
          }
        }
        > svg {
          flex: none;
        }
      }
      .error-item {
        background-color: @danger-0;
        color: @danger-600;
        > svg:first-child {
          fill: @danger-600;
          transform: scale(0.8);
        }
      }
    }
  }

  .icon {
    fill: @neutral-600;
    transform: scale(0.7);
    cursor: pointer;
  }

  @container (max-width: @breakpoint-mobile) {
    .document-upload {
      .document-upload__file-upload {
        flex-direction: column;
        flex-wrap: nowrap;
        align-items: stretch;

        .upload-item {
          width: auto;
        }

        .trash-container {
          > * {
            flex: 1;
          }
        }
      }
    }
  }
</style>
